import { useEffect, useState } from "react";
import type {
  AgentDemo,
  Document,
  KnowledgeProvider,
  QualitySummary,
  ViewNavigation,
} from "@cloudox/api-client";

export type AsyncState<T> =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: unknown }
  | { readonly status: "ready"; readonly value: T };

/** Tiny promise-to-state hook so the preview avoids a React Query dependency. */
function useAsync<T>(
  run: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: "loading" });
    run(controller.signal).then(
      (value) => {
        if (active) setState({ status: "ready", value });
      },
      (error: unknown) => {
        if (active && !controller.signal.aborted) setState({ status: "error", error });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export function useViewNavigation(provider: KnowledgeProvider): AsyncState<ViewNavigation> {
  return useAsync((signal) => provider.loadViewNavigation(signal), [provider]);
}

export function useQuality(provider: KnowledgeProvider): AsyncState<QualitySummary | null> {
  return useAsync((signal) => provider.loadQuality(signal), [provider]);
}

export function useAgentDemo(provider: KnowledgeProvider): AsyncState<AgentDemo | null> {
  return useAsync(
    (signal) => (provider.loadAgentDemo ? provider.loadAgentDemo(signal) : Promise.resolve(null)),
    [provider],
  );
}

export function useViewDocument(
  provider: KnowledgeProvider,
  view: string | undefined,
): AsyncState<Document> {
  return useAsync(
    (signal) =>
      view
        ? provider.loadViewDocument(view, signal)
        : Promise.reject(new Error("No view selected")),
    [provider, view],
  );
}

/**
 * Any document the run published, addressed by its manifest slug — used for the
 * cross-cutting pages (Environment Evolution, Cost Intelligence, Knowledge
 * Quality), which the provider resolves through `related_pages`.
 */
export function useDocumentSlug(
  provider: KnowledgeProvider,
  slug: string | undefined,
): AsyncState<Document> {
  return useAsync(
    (signal) =>
      slug
        ? provider.loadDocumentSlug(slug, signal)
        : Promise.reject(new Error("No document selected")),
    [provider, slug],
  );
}
